import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Facebook, Ticket, Menu as MenuIcon, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const D10 = {
  instagram: "https://www.instagram.com/d10spizza/",
  facebook: "https://www.facebook.com/d10spizza/",
};

const NAV_LINKS = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/menu", label: "Menu" },
  { href: "/blog", label: "Blog" },
  { href: "/join", label: "Join Us" },
  { href: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Check if we're on the academy page
  const isAcademyPage = location.pathname === "/academy" || location.pathname.startsWith("/academy/");

  // Choose logo based on current page
  const logoSrc = isAcademyPage ? "/d10-logo-academy.png" : "/d10logo.svg";

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const navShellClass = "fixed top-0 left-0 w-full z-50 transition-all duration-300 " +
    (scrolled
      ? "bg-white/95 backdrop-blur-md border-b border-blue-50 py-2 shadow-sm"
      : "bg-transparent py-4 sm:py-5");

  const desktopLinksWrapperClass = "hidden lg:flex items-center justify-center gap-8 xl:gap-12 font-manrope font-bold text-xs uppercase tracking-widest " +
    (scrolled ? "text-[#0F172A]" : "text-white");

  const desktopLinkClass = "hover:text-[#0087E6] transition-colors relative group py-2";

  // Logo filter logic:
  // - When scrolled or menu open: black (brightness-0)
  // - When NOT scrolled: white (brightness-0 invert)
  const logoFilterClass = (scrolled || isOpen)
    ? "brightness-0"
    : "brightness-0 invert";

  const logoShadowClass = (scrolled || isOpen)
    ? "drop-shadow-[0_8px_24px_rgba(15,23,42,0.10)]"
    : "drop-shadow-[0_10px_26px_rgba(0,0,0,0.35)]";

  return (
    <nav className={navShellClass}>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-2 items-center lg:grid-cols-[auto_1fr_auto]">
          <Link
            to="/"
            className="z-50 inline-flex items-center"
            aria-label="Go to home"
            onClick={() => setIsOpen(false)}
          >
            <img
              src={logoSrc}
              alt="D10"
              className={`h-14 sm:h-16 lg:h-20 w-auto transition-all duration-300 ${logoFilterClass} ${logoShadowClass}`}
              loading="eager"
              fetchpriority="high"
            />
          </Link>

          <div className={desktopLinksWrapperClass}>
            {NAV_LINKS.map(function(link) {
              return (
                <Link key={link.href} to={link.href} className={desktopLinkClass}>
                  {link.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0087E6] group-hover:w-full transition-all duration-300" />
                </Link>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center justify-end gap-3">
            <Link
              to="/reservations"
              className={"inline-flex items-center gap-2 px-6 py-2.5 font-bold rounded-full transition-all uppercase text-xs tracking-widest shadow-lg group " +
                (scrolled
                  ? "bg-[#0087E6] text-white hover:bg-[#0F172A]"
                  : "bg-white text-[#0087E6] hover:bg-[#0087E6] hover:text-white")}
            >
              <Ticket size={16} className="group-hover:rotate-12 transition-transform" />
              Reserve
            </Link>

            {/* DESKTOP ACADEMY BUTTON - Yellow background, black text, light pink dot */}
            <Link
              to="/academy"
              className="inline-flex items-center gap-2 px-6 py-2.5 font-bold rounded-full transition-all uppercase text-xs tracking-widest shadow-lg group bg-[#FFD700] text-black hover:bg-[#e6c200] hover:shadow-xl"
            >
              <span className="w-2 h-2 rounded-full bg-pink-300 group-hover:scale-110 transition-transform" />
              Academy
            </Link>
          </div>

          <button
            className={"lg:hidden z-50 p-2 rounded-xl transition-colors justify-self-end " +
              (isOpen ? "text-white" : scrolled ? "text-[#0087E6]" : "text-white")}
            onClick={() => setIsOpen(function(v) { return !v; })}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={28} strokeWidth={2.5} /> : <MenuIcon size={28} strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <React.Fragment>
            <motion.div
              className="fixed inset-0 z-40 bg-black/40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              className="fixed inset-x-0 top-0 z-50 h-screen bg-[#0087E6] text-white"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              role="dialog"
              aria-modal="true"
            >
              <div className="h-full flex flex-col">
                <div className="sticky top-0 z-10 bg-[#0087E6] border-b border-white/10 px-4 sm:px-6">
                  <div className="h-20 flex items-center justify-between">
                    <Link to="/" className="inline-flex items-center gap-3" onClick={() => setIsOpen(false)}>
                      <img src={logoSrc} alt="D10" className="h-14 w-auto brightness-0 invert" />
                    </Link>
                    <button
                      className="p-2 rounded-xl text-white hover:bg-white/10"
                      onClick={() => setIsOpen(false)}
                      aria-label="Close menu"
                    >
                      <X size={28} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-8 pt-6">
                  <div className="bg-white/10 border border-white/10 rounded-3xl overflow-hidden">
                    {NAV_LINKS.map(function(link) {
                      var active = location.pathname === link.href;
                      return (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={"flex items-center justify-between px-6 py-5 transition-colors " +
                            (active ? "bg-white/15" : "hover:bg-white/10")}
                        >
                          <span className="font-anton text-2xl uppercase">{link.label}</span>
                          <span className={"text-xs font-bold uppercase tracking-widest " + (active ? "text-white" : "text-white/70")}>
                            {active ? "Here" : "Go"}
                          </span>
                        </Link>
                      );
                    })}
                  </div>

                  <div className="mt-6 grid gap-3">
                    <Link
                      to="/reservations"
                      className="w-full font-anton text-xl uppercase text-[#0087E6] bg-white px-8 py-4 rounded-2xl shadow-xl hover:bg-[#0F172A] hover:text-white transition-colors flex items-center justify-center gap-3"
                    >
                      <Ticket size={20} />
                      Reserve
                    </Link>

                    {/* MOBILE ACADEMY BUTTON - Yellow background, black text, light pink dot */}
                    <Link
                      to="/academy"
                      className="w-full font-anton text-xl uppercase text-black bg-[#FFD700] px-8 py-4 rounded-2xl shadow-lg hover:bg-[#e6c200] hover:shadow-xl transition-all flex items-center justify-center gap-3 group"
                    >
                      <span className="w-2 h-2 rounded-full bg-pink-300 group-hover:scale-110 transition-transform" />
                      Academy
                    </Link>
                  </div>

                  <div className="mt-8 flex items-center justify-center gap-8">
                    <a href="https://www.instagram.com/d10spizza/" target="_blank" rel="noreferrer" className="text-white/80 hover:text-white transition-colors" aria-label="Instagram">
                      <Instagram size={28} />
                    </a>
                    <a href="https://www.facebook.com/d10spizza/" target="_blank" rel="noreferrer" className="text-white/80 hover:text-white transition-colors" aria-label="Facebook">
                      <Facebook size={28} />
                    </a>
                  </div>

                  <div className="mt-6 text-center text-white/60 text-xs">
                    Pag days friendly. No stress.
                  </div>
                </div>
              </div>
            </motion.div>
          </React.Fragment>
        )}
      </AnimatePresence>
    </nav>
  );
}